import type { Icon, IconProps } from "@phosphor-icons/react/dist/lib/types";
import { ArrowsOutIcon } from "@phosphor-icons/react/dist/csr/ArrowsOut";
import { CameraRotateIcon } from "@phosphor-icons/react/dist/csr/CameraRotate";
import { FilmSlateIcon } from "@phosphor-icons/react/dist/csr/FilmSlate";
import { MusicNotesIcon } from "@phosphor-icons/react/dist/csr/MusicNotes";
import { PackageIcon } from "@phosphor-icons/react/dist/csr/Package";
import { PaintBrushIcon } from "@phosphor-icons/react/dist/csr/PaintBrush";
import { PersonSimpleRunIcon } from "@phosphor-icons/react/dist/csr/PersonSimpleRun";
import { ScissorsIcon } from "@phosphor-icons/react/dist/csr/Scissors";
import { SelectionBackgroundIcon } from "@phosphor-icons/react/dist/csr/SelectionBackground";
import { ShuffleIcon } from "@phosphor-icons/react/dist/csr/Shuffle";
import { SpeakerHighIcon } from "@phosphor-icons/react/dist/csr/SpeakerHigh";
import { SpeedometerIcon } from "@phosphor-icons/react/dist/csr/Speedometer";
import { TranslateIcon } from "@phosphor-icons/react/dist/csr/Translate";
import { UserFocusIcon } from "@phosphor-icons/react/dist/csr/UserFocus";
import { UserSoundIcon } from "@phosphor-icons/react/dist/csr/UserSound";
import { EraserIcon } from "@phosphor-icons/react/dist/csr/Eraser";
import type { ImageTool } from "@/shared/lib/imageTools";
import { AudioIcon, CropIcon, ImageIcon, RefreshIcon, StackIcon, SunIcon, TextIcon, VideoIcon, WaveformIcon } from "./icons";

const imageIcons: Record<ImageTool, Icon> = {
  generate: ImageIcon,
  edit: PaintBrushIcon,
  upscale: ArrowsOutIcon,
  cinematic: FilmSlateIcon,
  variations: ShuffleIcon,
  skin: UserFocusIcon,
  camera: CameraRotateIcon,
  mockup: PackageIcon,
  background: SelectionBackgroundIcon,
  relight: SunIcon,
};

const videoIcons: Record<string, Icon> = {
  editor: ScissorsIcon, enhance: ArrowsOutIcon, modify: PaintBrushIcon, speak: UserSoundIcon,
  generate: VideoIcon, text_to_video: VideoIcon, image_to_video: ImageIcon,
  first_last_frame: StackIcon, multi_shot: FilmSlateIcon,
  reference_to_video: UserFocusIcon, motion_transfer: PersonSimpleRunIcon,
  extend: ArrowsOutIcon, clip_edit: ScissorsIcon, restyle: PaintBrushIcon,
  remove: EraserIcon, replace: ShuffleIcon, relight: SunIcon, upscale: ArrowsOutIcon,
  talking_photo: UserSoundIcon, lip_sync: UserSoundIcon,
  trim: ScissorsIcon, split: ScissorsIcon, join: StackIcon, speed: SpeedometerIcon,
  loop: RefreshIcon, crop: CropIcon, reframe: ArrowsOutIcon,
  captions: TextIcon, audio_tracks: WaveformIcon,
};

const audioIcons: Record<string, Icon> = {
  studio: AudioIcon, text_to_speech: AudioIcon, dialogue: UserSoundIcon,
  voice_design: UserSoundIcon, voice_clone: UserFocusIcon, voice_clone_professional: UserFocusIcon,
  voice_manage: UserSoundIcon, voice_revoke: UserSoundIcon, voice_change: ShuffleIcon,
  transcribe: TextIcon, forced_align: TextIcon, pronunciation: TranslateIcon,
  record: AudioIcon, trim: ScissorsIcon, split: ScissorsIcon, join: StackIcon,
  volume: SpeakerHighIcon, mix: StackIcon, normalize: WaveformIcon,
  speed: SpeedometerIcon, voice_isolate: UserSoundIcon, noise_cleanup: EraserIcon,
  source_separate: StackIcon, dubbing: TranslateIcon, subtitle_export: TextIcon,
};

/** Shared visual identity for a tool in both its picker and its active heading. */
export function CreationToolIcon({ media, tool, ...props }: Omit<IconProps, "weight"> & {
  media: "image" | "video" | "audio";
  tool?: string;
}) {
  const Icon = media === "image" ? imageIcons[tool as ImageTool] ?? ImageIcon
    : media === "video" ? videoIcons[tool ?? "generate"] ?? VideoIcon
      : audioIcons[tool ?? "studio"] ?? (tool?.startsWith("music_") ? MusicNotesIcon
        : tool?.startsWith("sound_") ? SpeakerHighIcon : WaveformIcon);
  return <Icon {...props} weight="bold" aria-hidden="true" focusable="false" data-creation-tool-icon={tool ?? media} />;
}
